import { useState, useRef, useEffect } from 'react';
import PAYERS from '../../data/payerList.js';

/**
 * Searchable payer combobox. Filters by payer name or EDI ID.
 * On selection, calls onSelect({ name, ediId }).
 * Falls back to free-text entry if the payer isn't in the list.
 */
export default function PayerCombobox({ value, onChange, error }) {
  const [query, setQuery] = useState(value || '');
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // Sync external value changes (e.g. form reset)
  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  const filtered =
    query.length < 1
      ? PAYERS
      : PAYERS.filter(
          (p) =>
            p.name.toLowerCase().includes(query.toLowerCase()) ||
            p.ediId.toLowerCase().includes(query.toLowerCase()),
        ).slice(0, 10);

  const handleSelect = (payer) => {
    setQuery(payer.name);
    setOpen(false);
    onChange(payer);
  };

  const handleBlur = (e) => {
    // Close only if focus left the entire container
    if (!containerRef.current?.contains(e.relatedTarget)) {
      setOpen(false);
      // If the user typed something that doesn't match a payer exactly, keep as free text
      if (!PAYERS.some((p) => p.name === query)) {
        onChange({ name: query, ediId: null });
      }
    }
  };

  return (
    <div ref={containerRef} className="relative" onBlur={handleBlur}>
      <input
        type="text"
        className={`input w-full ${error ? 'border-red-500' : ''}`}
        placeholder="Search by payer name or EDI ID…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          onChange({ name: e.target.value, ediId: null });
        }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />

      {open && filtered.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filtered.map((payer) => (
            <li key={`${payer.name}-${payer.ediId}`}>
              <button
                type="button"
                className="w-full text-left px-4 py-2.5 hover:bg-neutral-50 dark:hover:bg-neutral-700/60 transition-colors"
                onMouseDown={(e) => e.preventDefault()} // prevent blur before click
                onClick={() => handleSelect(payer)}
              >
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {payer.name}
                </span>
                {payer.ediId && (
                  <span className="ml-2 text-xs text-neutral-400 dark:text-neutral-500 font-mono">
                    {payer.ediId}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
